import React, { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { getRouteMetadata } from "../seo/routeMeta";

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  keywords?: string;
  ogImage?: string;
  structuredData?: object | object[];
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  canonical,
  keywords = "一站式到校課程, 學校方案, 到校興趣班, 到校工作坊, 學校到校服務, 課後興趣班, 駐校開班, 學校興趣班, 學校工作坊, 中小學到校課程, 到校手工藝課程, 到校蠟燭製作, 到校植物護理, 興趣班, 工作坊, 興趣班工作坊, 興趣班香港, 工作坊香港, 香港興趣班, 香港工作坊, 手工興趣班, 手工工作坊, 手工藝興趣班, 手工藝工作坊, 蠟燭製作興趣班, 蠟燭製作工作坊, 植物護理興趣班, 植物護理工作坊, 中小學興趣班, 中小學工作坊, 成人興趣班, 成人工作坊, 香港手工藝, 香港手工課程, 蠟燭製作課程, 植物護理課程, 手工藝班, 香港手工班, 中小學手工課程, 成人手工班, 手工創作, 香港craft classes, Hong Kong workshops, candle making Hong Kong, plant care Hong Kong, 香港手工藝課程推薦, 香港手工興趣班推薦, 香港工作坊推薦",
  ogImage = "https://cosylabdesigns.com/icon/icon.png",
  structuredData,
}) => {
  const location = useLocation();
  const fallbackMeta = getRouteMetadata(location.pathname);
  const resolvedTitle = title || fallbackMeta.title;
  const resolvedDescription = description || fallbackMeta.description;
  const canonicalHref = canonical || fallbackMeta.canonical;

  useLayoutEffect(() => {
    // Update HTML lang attribute
    document.documentElement.lang = "zh-Hant";

    // Update document title
    document.title = resolvedTitle;

    // Update or create canonical tag
    let canonicalLink = document.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.rel = "canonical";
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalHref;

    // Update meta description
    let metaDescription = document.querySelector(
      'meta[name="description"]'
    ) as HTMLMetaElement;
    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.name = "description";
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = resolvedDescription;

    // Update meta keywords
    let metaKeywords = document.querySelector(
      'meta[name="keywords"]'
    ) as HTMLMetaElement;
    if (!metaKeywords) {
      metaKeywords = document.createElement("meta");
      metaKeywords.name = "keywords";
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = keywords;

    // Update robots meta tag
    let metaRobots = document.querySelector(
      'meta[name="robots"]'
    ) as HTMLMetaElement;
    if (!metaRobots) {
      metaRobots = document.createElement("meta");
      metaRobots.name = "robots";
      document.head.appendChild(metaRobots);
    }
    metaRobots.content = "index, follow";

    // Update Open Graph tags
    const updateOGTag = (property: string, content: string) => {
      let ogTag = document.querySelector(
        `meta[property="${property}"]`
      ) as HTMLMetaElement;
      if (!ogTag) {
        ogTag = document.createElement("meta");
        ogTag.setAttribute("property", property);
        document.head.appendChild(ogTag);
      }
      ogTag.content = content;
    };

    updateOGTag("og:type", "website");
    updateOGTag("og:title", resolvedTitle);
    updateOGTag("og:description", resolvedDescription);
    updateOGTag("og:url", canonicalHref);
    updateOGTag("og:image", ogImage);

    // Update Twitter tags
    const updateTwitterTag = (name: string, content: string) => {
      let twitterTag = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement;
      if (!twitterTag) {
        twitterTag = document.createElement("meta");
        twitterTag.setAttribute("name", name);
        document.head.appendChild(twitterTag);
      }
      twitterTag.content = content;
    };

    updateTwitterTag("twitter:card", "summary_large_image");
    updateTwitterTag("twitter:title", resolvedTitle);
    updateTwitterTag("twitter:description", resolvedDescription);
    updateTwitterTag("twitter:image", ogImage);
    updateTwitterTag("twitter:url", canonicalHref);

    // Add geo-location meta tags for Hong Kong
    const updateGeoTag = (name: string, content: string) => {
      let geoTag = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement;
      if (!geoTag) {
        geoTag = document.createElement("meta");
        geoTag.setAttribute("name", name);
        document.head.appendChild(geoTag);
      }
      geoTag.content = content;
    };

    updateGeoTag("geo.region", "HK");
    updateGeoTag("geo.placename", "Hong Kong");
    updateGeoTag("ICBM", "22.3193, 114.1694"); // Hong Kong coordinates

    // Handle structured data (JSON-LD)
    if (structuredData) {
      // Remove ALL JSON-LD injected by React
      const existingScripts = document.querySelectorAll(
        'script[type="application/ld+json"][data-react-seo]'
      );
      existingScripts.forEach((script) => script.remove());

      const dataArray = Array.isArray(structuredData)
        ? structuredData
        : [structuredData];

      dataArray.forEach((data) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-react-seo", "true");
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      });
    }
  }, [
    resolvedTitle,
    resolvedDescription,
    canonical,
    keywords,
    ogImage,
    canonicalHref,
    structuredData,
  ]);

  return null; // This component doesn't render anything
};

export default SEOHead;
